// ============================================================================
// LA VEILLE DES DÉFAILLANCES SUR LA FICHE CLIENT (SA-9.3) — un bandeau en tête de fiche, une ligne dans l'Identité.
//
// ⚠️ UNE LECTURE, DEUX ENDROITS : la fiche lit les annonces du client une fois (`useTenantLegal`) et les passe au bandeau et à
//    l'onglet Identité. Deux lectures pourraient se contredire — le bandeau rouge d'un côté, « aucune annonce » de l'autre.
// ⚠️ LE BANDEAU NE S'AFFICHE QUE POUR UN SIGNALEMENT : rouge tant qu'il attend sa décision, neutre une fois tranché (il
//    rappelle alors la suite et son motif). Une annonce de l'historique (plus de douze mois quand la veille l'a vue) ne
//    demande rien : elle se lit dans l'Identité, pas en tête de fiche.
// ⚠️ TROIS ÉTATS QUI NE SE CONFONDENT PAS dans l'Identité : NON SURVEILLÉ (aucun SIREN, ni au client ni à ses magasins),
//    surveillé sans annonce — avec la date du dernier passage, jamais « cette nuit » qu'on ne sait pas —, et des annonces.
// ============================================================================
import { useEffect, useState, type ReactNode } from 'react';
import { Icon } from '@orgazone/ui-web';
import { api, type LegalEventRow, type LegalSignal, type TenantLegalView } from '../api';
import { can, useAuth } from '../auth';
import { dateAnnonce, SUITES } from './defaillance-modal';

/** Les annonces d'un client, relues quand une décision a été prise (`refreshKey`). */
export function useTenantLegal(tenantId: string, refreshKey: number): { view: TenantLegalView | null; error: string | null } {
  const [view, setView] = useState<TenantLegalView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.tenantLegalEvents(tenantId)
      .then((r) => { if (alive) { setView(r); setError(null); } })
      .catch((e) => { if (alive) { setView(null); setError((e as Error).message); } });
    return () => { alive = false; };
  }, [tenantId, refreshKey]);
  return { view, error };
}

/** Ce qu'une annonce est devenue : à trancher, à l'historique, ou la suite donnée. */
export function stateOf(e: LegalEventRow): { label: string; tone: 'da' | 'ne' | 'wa' | 'in' } {
  if (!e.signal) return { label: 'historique', tone: 'ne' };
  if (!e.decision) return { label: 'à trancher', tone: 'da' };
  return { label: SUITES.find((s) => s.k === e.decision)!.label.toLowerCase(), tone: e.decision === 'warn' ? 'wa' : 'in' };
}

/**
 * Le signalement que montre le bandeau : le plus récent qui attend sa décision, sinon la dernière décision prise ;
 * `null` quand le client n'a aucun signalement (rien en tête de fiche). `others` : combien d'autres attendent encore.
 */
export function bannerSignal(events: readonly LegalEventRow[]): { event: LegalEventRow; open: boolean; others: number } | null {
  const byPublished = (a: LegalEventRow, b: LegalEventRow) => (b.publishedOn ?? '').localeCompare(a.publishedOn ?? '');
  const open = events.filter((e) => e.signal && !e.decision).sort(byPublished);
  if (open.length) return { event: open[0]!, open: true, others: open.length - 1 };
  const decided = events.filter((e) => e.signal && e.decision).sort((a, b) => (b.decidedAt ?? '').localeCompare(a.decidedAt ?? ''));
  return decided.length ? { event: decided[0]!, open: false, others: 0 } : null;
}

const sourceName = (s: LegalEventRow['source']) => (s === 'bodacc' ? 'BODACC' : 'répertoire Sirene');
const lower = (s: string) => `${s.charAt(0).toLowerCase()}${s.slice(1)}`;

/** Le bandeau en tête de fiche — rien tant que la lecture n'a pas abouti, sauf son échec, qui se dit. */
export function ClientLegalBanner({ view, error, tenantId, tenantName, onDecide }: {
  view: TenantLegalView | null; error: string | null; tenantId: string; tenantName: string;
  /** Ouvre la décision — la modale vit dans la page, qui relit la fiche après. */
  onDecide: (s: LegalSignal) => void;
}) {
  const { user } = useAuth();
  // Trancher appartient à qui tient l'abonnement des clients ; les autres lisent, et le savent.
  const canDecide = can(user, 'console.killswitch.subscription');
  if (error) {
    return <div className="legal-banner neutral"><div className="legal-banner-body"><div className="legal-banner-title">Veille des défaillances illisible</div><div className="legal-banner-text">{error}</div></div></div>;
  }
  const b = view ? bannerSignal(view.events) : null;
  if (!b) return null;
  const e = b.event;
  const suite = e.decision ? SUITES.find((s) => s.k === e.decision)!.label.toLowerCase() : null;
  const published = e.publishedOn ? `${e.source === 'bodacc' ? 'publiée au BODACC' : 'déclarée au répertoire Sirene'} le ${dateAnnonce(e.publishedOn)}` : `relevée au ${sourceName(e.source)}, sans date`;
  return (
    <div className={b.open ? 'legal-banner' : 'legal-banner neutral'} role={b.open ? 'alert' : undefined}>
      <span className="legal-banner-ico" aria-hidden="true">
        <Icon name="alert" size={16} />
      </span>
      <div className="legal-banner-body">
        <div className="legal-banner-title">
          {b.open ? `${e.nature} — ${published}` : `${e.source === 'bodacc' ? 'BODACC' : 'Sirene'} · ${lower(e.nature)} — décision enregistrée`}
        </div>
        <div className="legal-banner-text">
          {b.open
            ? <>{e.judgmentDate && e.source === 'bodacc' ? `Jugement du ${dateAnnonce(e.judgmentDate)}${e.court ? `, ${e.court}` : ''}. ` : e.court ? `${e.court}. ` : ''}Aucun blocage n’a été posé : une procédure collective n’est pas un impayé. La suite se décide ici.{b.others > 0 ? ` Et ${b.others} autre${b.others > 1 ? 's' : ''} annonce${b.others > 1 ? 's' : ''} à trancher.` : ''}</>
            : <>Suite donnée : {suite} — « {e.reason} »{e.decidedBy ? `, ${e.decidedBy}` : ''}{e.decidedAt ? ` le ${dateAnnonce(e.decidedAt.slice(0, 10))}` : ''}. Consignée au journal.</>}
        </div>
        <div className="legal-banner-ref">{e.source === 'bodacc' ? `BODACC · annonce ${e.externalId} · SIREN ${e.siren}` : `Sirene · SIREN ${e.siren}`}</div>
      </div>
      <div className="legal-banner-actions">
        {e.url && <a href={e.url} target="_blank" rel="noopener noreferrer" className="btn-ghost sm">Voir l’annonce ↗</a>}
        {b.open && (canDecide
          ? <button type="button" className="btn danger sm" onClick={() => onDecide({
            id: e.id, tenantId, tenantName, source: e.source, siren: e.siren, nature: e.nature, court: e.court,
            judgmentDate: e.judgmentDate, publishedOn: e.publishedOn, url: e.url,
          })}>Décider de la suite</button>
          : <span className="legal-banner-note">décision réservée à la facturation</span>)}
      </div>
    </div>
  );
}

/**
 * La ligne « BODACC » de l'onglet Identité : la dernière annonce et ce qu'elle est devenue, une ligne par annonce
 * s'il y en a plusieurs (une procédure en publie souvent plusieurs : ouverture, plan, clôture).
 */
export function legalFact(view: TenantLegalView | null, error: string | null): ReactNode {
  if (error) return <span style={{ color: 'var(--wa-fg)' }}>veille illisible — {error}</span>;
  if (!view) return '…';
  if (!view.watched) return '— non surveillé : aucun SIREN';
  if (view.events.length === 0) {
    return view.lastRunAt ? `aucune annonce · veille du ${dateAnnonce(view.lastRunAt.slice(0, 10))}` : '— veille jamais passée';
  }
  return (
    <>
      {view.events.map((e) => {
        const st = stateOf(e);
        return (
          <div key={e.id} style={{ color: st.tone === 'da' ? 'var(--da-fg)' : undefined }}>
            {lower(e.nature)} · {e.publishedOn ? dateAnnonce(e.publishedOn) : 'sans date'} · {st.label}
          </div>
        );
      })}
    </>
  );
}
